import { t } from "@lingui/macro";
import { useLinkTo, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useAtom, useSetAtom } from "jotai";
import { useCallback } from "react";
import { Platform } from "react-native";

import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  isLocallySignedOutAtom,
  isUserAuthenticated_atom,
  preAuthenticatedPath_atom,
} from "../features/Authentication/atoms";
import { useSignInMutation$data } from "../features/Authentication/hooks/__generated__/useSignInMutation.graphql";
import { executePostLoginRedirect } from "../features/Authentication/utils/executePostLoginRedirect";
import { resolvePostLoginRedirect } from "../features/Authentication/utils/resolvePostLoginRedirect";
import { clearPersistedNativeSession } from "../features/BiometricAuthentication/biometricUnlock";
import { sessionQueryVersionAtom } from "../features/Session";
import { BaseNavigatorParamList } from "../navigators/BaseNavigator/types";
import { buildStaffAppUrl } from "../utils/getStaffAppUrlString";

/**
 * This was extracted from the useSignIn hook because we are now passing in a callback
 * to handle what happens after the user signs in.  The AuthenticationModal component
 * does a slightly different thing.  After we migrate the whole app to use the Authentication
 * modal we can probably get rid of this hook.
 */
export const useCompleteLogin = () => {
  const setIsLocallySignedOut = useSetAtom(isLocallySignedOutAtom);
  const setIsUserAuthenticated = useSetAtom(isUserAuthenticated_atom);
  const incrementSessionQueryVersion = useSetAtom(sessionQueryVersionAtom);
  const [preAuthenticatedPath, setPreAuthenticatedPath] = useAtom(
    preAuthenticatedPath_atom
  );

  const appNavigation =
    useNavigation<NativeStackNavigationProp<BaseNavigatorParamList>>();
  const linkTo = useLinkTo();
  const { showToast } = useToast();

  // I don't like having to use this type here but not sure another way to get this data
  // with out having to create a new derived type from the graphql data.
  // We can't use a fragment here because we are in a callback.
  const onLoginCompleted = useCallback(
    (data: useSignInMutation$data) => {
      if (data.responsePayload?.userType === "MEMBER") {
        setIsLocallySignedOut(false);
        //TODO: Clean up how we decide a user is authed. We used different ways,
        //and many of them seem unnecessary now.
        setIsUserAuthenticated(true);
        incrementSessionQueryVersion((current) => current + 1);

        const session = data.responsePayload?.session;
        const firstProfile = session?.user?.memberProfiles?.edges?.[0]?.node;
        const defaultClubSlug = firstProfile?.member?.club?.slug;
        const redirectPath = resolvePostLoginRedirect({
          locationSearch:
            Platform.OS === "web" ? window.location.search : undefined,
          preAuthenticatedPath,
        });
        const didRedirect = executePostLoginRedirect({
          clearPreAuthenticatedPath: () => setPreAuthenticatedPath(null),
          redirectExternal: (url) => {
            if (Platform.OS === "web") {
              window.location.href = url;
              return;
            }

            appNavigation.navigate("Club", {
              clubSlug: defaultClubSlug ?? "",
              screen: "Home",
              params: {
                screen: "WebView",
                params: {
                  title: t`Continue`,
                  url,
                },
              },
            });
          },
          redirectInApp: (path) => {
            linkTo(path);
          },
          redirectPath,
        });

        if (!didRedirect) {
          if (defaultClubSlug) {
            appNavigation.navigate("Club", {
              clubSlug: defaultClubSlug,
              screen: "Home",
              params: { screen: "MainHome" },
            });
          } else {
            // TODO: What do we do if we don't have a club to go to?
          }
        }
      } else {
        if (Platform.OS === "web") {
          window.location.href = buildStaffAppUrl(window.location);
        } else {
          clearPersistedNativeSession().then(() => {
            setIsUserAuthenticated(false);
            appNavigation.navigate("Unauthenticated");
            showToast({
              description: t`Whoops! For now, only members can log into the mobile application.`,
              semanticStyle: "error",
              toastId: "staff-login-error",
            });
          });
        }
      }
    },
    [
      appNavigation,
      linkTo,
      preAuthenticatedPath,
      incrementSessionQueryVersion,
      setIsLocallySignedOut,
      setIsUserAuthenticated,
      setPreAuthenticatedPath,
      showToast,
    ]
  );

  return onLoginCompleted;
};
